import React from 'react'
import { GetServerSideProps, GetServerSidePropsContext } from 'next'

import { Layout } from 'components/layout'
import { TagsCloud } from '@components/tags-cloud'
import { Biography } from '@components/pages/autor/biography'
import { HeadMetadata } from 'components/head-metadata'

import { AuthorsProps } from 'services/api/authors-pages'
import { MenusApi, Menu } from '@services/api/menus'
import { HeaderSection } from '@components/header-section'
import { AuthorArticlesList } from '@components/author-articles-list'

import styles from './styles.module.scss'
import { createUrlParamsSwr } from '@utils/create-url-params-swr'
import { AuthorArticlesPagination } from '@services/api/authors'
import useSWR from 'swr'
import { fetcherDataApi } from '@utils/fetcher-data-api'
import { Pagination } from '@components/shared/pagination'
import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'
import { SponsorBanner } from '@components/sponsor-banner'

interface Props {
  initialData: AuthorArticlesPagination
  name: string
  metaTitle: string
  metaDescription: string
  biography: string
  avatar: string
  slug: string
  tags: any[]
  main: Menu[]
  footer1: Menu[]
  footer2: Menu[]
  canonicalUrl: string
  url: string
  ads: AdsIndexDto[]
}

const AuthorPage = ({
  name,
  metaTitle,
  metaDescription,
  biography,
  avatar,
  tags,
  canonicalUrl,
  initialData,
  url,
  main,
  footer1,
  footer2,
  ads
}: Props) => {
  const { data } = useSWR(url, fetcherDataApi, {
    initialData
  })

  const { articles, total, page, pageSize } = data

  return (
    <Layout
      sponsorKeyword="home"
      mainMenus={main}
      footerMenus1={footer1}
      footerMenus2={footer2}
      logoHeading={true}
      logoUrl="/noticias"
      ad={ads.find(ad => ad.code === 'ad_sponsor_top')}
    >
      <HeadMetadata
        title={metaTitle || name}
        metaTitle={metaTitle}
        metaDescription={metaDescription}
        canonicalUrl={canonicalUrl}
        coverImage={avatar}
      />

      <HeaderSection currentDate={true} className="py-4" />
      <div className={styles.container}>
        <div className={styles.authorContainer}>
          <Biography
            name={name}
            avatarUrl={avatar}
            biography={biography}
            className="mx-auto"
          />
        </div>
        <div className={styles.listOfArticles}>
          {/* Container com flex-row reverse no mobile */}
          <AuthorArticlesList articles={articles} />
          <Pagination currentPage={page} pageSize={pageSize} total={total} />
        </div>
      </div>
      <SponsorBanner
        config={ads.find(ad => ad.code === 'ad_sponsor_middle')}
        className="my-10"
      />
      <SponsorBanner
        config={ads.find(ad => ad.code === 'ad_sponsor_bottom')}
        className="my-10"
      />
      <TagsCloud tags={tags} className="my-10" />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  query
}: GetServerSidePropsContext<{ author: string }>) => {
  try {
    const { error, ...props } = await AuthorsProps(params.author)
    const menus = await MenusApi.all()

    const {
      data: { ads }
    } = await AdsApi.index({
      codes: ['ad_sponsor_top', 'ad_sponsor_middle', 'ad_sponsor_bottom']
    })

    if (error?.status === 404) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        ...props,
        ...menus,
        ...createUrlParamsSwr({
          query,
          per: 16,
          path: `/authors/${params.author}/articles`
        }),
        ads: ads || null
      }
    }
  } catch (error) {
    return {
      notFound: true
    }
  }
}

export default AuthorPage
